import { forwardRef, cloneElement, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { useSpring, animated } from "@react-spring/web";
import FormHelperText from "@mui/material/FormHelperText";
import OutlinedTextField from "./OutlinedTextField";
import SelectInput from "./SelectInput";
import CustomizedButton from "./CustomizedButton";
import PasswordChange from "./PasswordChange";
import config from "../config";

const Fade = forwardRef(function Fade(props, ref) {
    const { children, in: open, onClick, onEnter, onExited, ownerState, ...other } = props;
    const style = useSpring({
        from: { opacity: 0 },
        to: { opacity: open ? 1 : 0 },
        onStart: () => {
            if (open && onEnter) {
                onEnter(null, true);
            }
        },
        onRest: () => {
            if (!open && onExited) {
                onExited(null, true);
            }
        },
    });

    return (
        <animated.div ref={ref} style={style} {...other}>
            {cloneElement(children, { onClick })}
        </animated.div>
    );
});

Fade.propTypes = {
    children: PropTypes.element.isRequired,
    in: PropTypes.bool,
    onClick: PropTypes.any,
    onEnter: PropTypes.func,
    onExited: PropTypes.func,
    ownerState: PropTypes.any,
};

const style = {
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    color: "white",
    width: 560,
    bgcolor: "black",
    border: "1px solid white",
    boxShadow: 24,
    p: 4,
};

export default function PasswordCheck({ openModal, handleOpen, handleClose }) {
    const [open, setOpen] = useState(openModal);

    const [id, setId] = useState("");
    const [idError, setIdError] = useState("");
    const [passwordQuestion, setPasswordQuestion] = useState("");
    const [pwQError, setPwQError] = useState("");
    const [passwordAnswer, setPasswordAnswer] = useState("");
    const [pwAnsError, setPwAnsError] = useState("");
    const [password, setPassword] = useState("");

    const pwQInput2 = document.querySelector("[name=pwQ]");
    const pwAInput2 = document.querySelector("[name=pwAInput2]");

    const pwQFocus = () => {
        pwQInput2.focus();
    };

    const pwAFocus = () => {
        pwAInput2.focus();
    };
    const gotoPwQInput = (e) => {
        if (e.key === "Enter") {
            pwQFocus();
        }
    };
    const gotoPwAInput = (e) => {
        if (e.key === "Enter") {
            pwAFocus();
        }
    };

    const [passwordSearch, setPasswordSearch] = useState(false);

    const [openPwModal, setOpenPwModal] = useState(false);

    const handlePwOpen = () => {
        setOpenPwModal(true);
    };
    const handlePwClose = () => {
        setOpenPwModal(false);
    };

    const handleClose2 = () => {
        setOpen(false);
        handleClose();
    };

    const passwordSearchSubmit = () => {
        if (handlePasswordCheck()) {
            axios
                .post(`${config.API_URL}/passwordSearch`, {
                    member_id: id,
                    pw_question: passwordQuestion,
                    pw_answer: passwordAnswer,
                })
                .then((res) => {
                    if (res.data === 1) {
                        axios
                            .get(`${config.API_URL}/selectMember?member_id=${id}`)
                            .then((res) => {
                                setPassword(res.data.member_pw);
                            });
                        setPasswordSearch(true);
                        handlePwOpen();
                    } else {
                        alert("No matching information found!");
                    }
                })
                .catch((e) => {
                    console.error(e);
                });
        }
    };
    const isValidId = (email) => {
        const emailRegex =
            /([\w-.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/;
        return emailRegex.test(email);
    };

    const handlePasswordCheck = (event) => {
        let check = true;
        let validEmail =
            /([\w-.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/.test(
                id,
            );

        if (!id) {
            setIdError("Please enter your email.");
            check = false;
        } else if (!validEmail) {
            setIdError("Please enter a valid email address.");
            check = false;
        }
        if (passwordAnswer === "") {
            setPwAnsError("Please enter your security answer.");
            check = false;
        } else {
            setPwAnsError("");
        }
        if (passwordQuestion === "") {
            setPwQError("Please select a security question.");
            check = false;
        } else {
            setPwQError("");
        }
        return check;
    };

    const checkenterSubmit = (e) => {
        if (e.key === "Enter") {
            passwordSearchSubmit();
        }
    };

    return (
        <div>
            <Modal
                aria-labelledby="spring-modal-title"
                aria-describedby="spring-modal-description"
                open={open}
                onClose={handleClose2}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        TransitionComponent: Fade,
                    },
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Typography sx={{ mx: "auto", mb: 3 }} id="spring-modal-title" variant="h5" component="h2">
                            Forgot Password
                        </Typography>
                        <Box sx={{ display: "flex" }}>
                            <Typography sx={{ width: "150px", mr: 5, mt: 3 }} variant="h10" component="h4">
                                Email Address
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <OutlinedTextField
                                    name="email2"
                                    onKeyPress={gotoPwQInput}
                                    required
                                    isValidId={isValidId}
                                    value={id}
                                    onChange={setId}
                                    setIdError={setIdError}
                                    label="Enter your email address"
                                />
                                <FormHelperText sx={{ mt: -3, mb: 2, color: "red", fontSize: "0.9em" }}>
                                    {idError}
                                </FormHelperText>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex" }}>
                            <Typography sx={{ width: "150px", mr: 5, mt: 3 }} variant="h10" component="h4">
                                Security Question
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <SelectInput
                                    required
                                    name="pwQInput2"
                                    onKeyPress={gotoPwAInput}
                                    setPwQError={setPwQError}
                                    passwordQuestion={passwordQuestion}
                                    setPasswordQuestion={setPasswordQuestion}
                                />
                                <FormHelperText sx={{ mt: 1, mb: 2, color: "red", fontSize: "0.9em" }}>
                                    {pwQError}
                                </FormHelperText>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex" }}>
                            <Typography sx={{ width: "150px", mr: 5, mt: 3 }} variant="h10" component="h4">
                                Security Answer
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", mt: -1 }}>
                                <OutlinedTextField
                                    required
                                    name="pwAInput2"
                                    value={passwordAnswer}
                                    setPwAnsError={setPwAnsError}
                                    onChange={setPasswordAnswer}
                                    onKeyPress={checkenterSubmit}
                                    label="Enter the answer to your security question"
                                />
                                <FormHelperText sx={{ mt: -2, mb: 2, color: "red", fontSize: "0.9em" }}>
                                    {pwAnsError}
                                </FormHelperText>
                            </Box>
                        </Box>
                        <Box sx={{ mx: "auto", width: 50 }}>
                            <CustomizedButton
                                label="Find"
                                value="passwordAnswer"
                                onClick={passwordSearchSubmit}
                            ></CustomizedButton>
                            {passwordSearch ? (
                                <PasswordChange
                                    label="forgot-password"
                                    passwordChangeEmail={id}
                                    setPasswordSearch={setPasswordSearch}
                                    openPwModal={openPwModal}
                                    setOpenPwModal={setOpenPwModal}
                                    handlePwOpen={handlePwOpen}
                                    handlePwClose={handlePwClose}
                                    handleCloseAll={handleClose2}
                                    password={password}
                                />
                            ) : null}
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </div>
    );
}
